import { collection, doc, writeBatch, serverTimestamp, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { computeBadge } from './driverBadge';
import { maxBid } from './orderPricing';
import { calculateBreakdown, haversineKm } from './priceCalculator';
import { hourlyTotal } from './orderHourly';

// Felles bud-logikk for sjåførens budskjema: validering, innhold i budet og selve skrivingen.

// Feil med en tekst som kan vises rett til sjåføren
export class BidError extends Error {
  constructor(message) {
    super(message);
    this.name = 'BidError';
    this.userMessage = message;
  }
}

// Feilmelding for beløpet, eller null når det kan sendes. Bud over fastpris-taket, ikke-heltall,
// null/negativt og uendret bud (ved endring) avvises.
export function validateBid(rawAmount, order, existingBid = null) {
  const amt = Number(rawAmount);
  if (!amt || amt <= 0) return 'Angi et gyldig beløp';
  const cap = maxBid(order) ?? Infinity;
  if (amt > cap) {
    return `Budet kan ikke være over fastprisen (kr ${order.fixedPrice.toLocaleString('nb-NO')}).`;
  }
  if (!Number.isInteger(amt)) return 'Beløpet må være et helt tall';
  if (existingBid && amt === existingBid.amount) {
    return 'Budet er uendret. Velg et nytt beløp eller trekk budet.';
  }
  return null;
}

// Dokumentet som lagres i «bids». Må passe firestore.rules (heltall > 0, driverUid = innlogget).
export function buildBidData({ order, uid, amount, message = '', userDoc }) {
  const badge = computeBadge({
    completedOrders:        userDoc?.completedOrders        ?? 0,
    cancellationTimestamps: userDoc?.cancellationTimestamps ?? [],
    tippiVerified:          userDoc?.driverStatus === 'active',
  });
  return {
    orderId:               order.id,
    driverUid:             uid,
    amount,
    message:               message.trim(),
    driverBadge:           badge,
    driverTippiVerified:   userDoc?.driverStatus === 'active',
    driverCompletedOrders: userDoc?.completedOrders ?? 0,
    driverName:            userDoc?.name ?? '',
    createdAt:             serverTimestamp(),
  };
}

export function bidErrorMessage(err) {
  if (err instanceof BidError) return err.userMessage;
  return err?.code === 'permission-denied'
    ? 'Oppdraget tildeles nå direkte — bruk «Godta fastpris».'
    : 'Kunne ikke sende bud. Prøv igjen.';
}

// Sender (eller erstatter) budet. Reglene tillater ikke å endre et bud, så en endring er
// sletting + nytt bud i én skriving. Returnerer 'sent' | 'updated'; kaster feil med visbar tekst.
export async function submitBid({ order, existingBid = null, amount, message = '', uid, userDoc }) {
  const error = validateBid(amount, order, existingBid);
  if (error) throw new BidError(error);

  try {
    if (!existingBid) {
      // Sjekk om sjåfør allerede har budt på dette oppdraget
      const existing = await getDocs(
        query(collection(db, 'bids'), where('orderId', '==', order.id), where('driverUid', '==', uid))
      );
      if (!existing.empty) throw new BidError('Du har allerede lagt inn bud på dette oppdraget');
    }

    const batch = writeBatch(db);
    if (existingBid?.bidId) batch.delete(doc(db, 'bids', existingBid.bidId));
    batch.set(doc(collection(db, 'bids')), buildBidData({ order, uid, amount: Number(amount), message, userDoc }));
    await batch.commit();
    return existingBid ? 'updated' : 'sent';
  } catch (err) {
    if (err instanceof BidError) throw err;
    console.error(err);
    throw new BidError(bidErrorMessage(err));
  }
}

export async function withdrawBid(bidId) {
  if (!bidId) throw new BidError('Kunne ikke trekke budet. Prøv igjen.');
  try {
    await deleteDoc(doc(db, 'bids', bidId));
  } catch {
    throw new BidError('Kunne ikke trekke budet. Prøv igjen.');
  }
}

// Veiledende forslag til beløp (vises som plassholder): anslått jobbverdi fra luftlinje, aldri over taket.
export function suggestedBidAmount(order, baseRate) {
  // Timeoppdrag: forslaget er timepris × timer (ikke avstandsprisen). Uten mengde finnes det ikke noe avstandsforslag.
  if (order?.hourly) {
    const total = hourlyTotal(order.hourly.hours, order.hourly.rate);
    return total == null ? null : Math.min(total, maxBid(order) ?? Infinity);
  }
  if (!(order?.estimatedTons > 0)) return null;
  const a = order?.pickupAddress, b = order?.deliveryAddress;
  if (a?.lat == null || b?.lat == null) return null;
  const km = haversineKm(a.lat, a.lng, b.lat, b.lng);
  if (!km) return null;
  const total = Math.round(
    calculateBreakdown(km, order.massType, order.estimatedTons, 'diesel', baseRate, order.removal === true).total
  );
  if (!Number.isFinite(total) || total <= 0) return null;
  return Math.min(total, maxBid(order) ?? Infinity);
}
